import React from 'react';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckCircle2Icon,
  SaveIcon } from
'lucide-react';
import { AppBar } from '../AppBar';
import { StepTimeline } from './StepTimeline';
import { usePortal } from '../../contexts/PortalContext';
import { PHASES, WIZARD_STEPS, type WizardStep } from '../../data/wizardSteps';

interface NavTarget {
  label: string;
  subtitle: string;
  onClick: () => void;
}

interface WizardLayoutProps {
  step: WizardStep;
  previous?: NavTarget;
  next?: NavTarget;
  children: React.ReactNode;
}

export function WizardLayout({ step, previous, next, children }: WizardLayoutProps) {
  const { save, lastSaved } = usePortal();
  const position = WIZARD_STEPS.findIndex((s) => s.id === step.id) + 1;
  const phase = PHASES.find((p) => p.n === step.phase);

  return (
    <div className="page">
      <AppBar />

      <div className="container wizard-grid">
        <aside className="wizard-aside">
          <StepTimeline currentStepId={step.id} />
        </aside>

        <main className="wizard-main">
          <header className="step-header">
            <div className="step-header-top">
              <span className="step-eyebrow">
                {phase ? `Step ${phase.n} - ${PHASES.length} · ${phase.label} · ` : ''}
                Section {position} of {WIZARD_STEPS.length}
              </span>
            </div>
            <h1 className="page-title">{step.label}</h1>
            <p className="page-lede">{step.summary}</p>
          </header>

          <div className="stack">{children}</div>

          <div className="wizard-footer">
            {previous ?
            <button type="button" className="nav-btn" onClick={previous.onClick}>
                <ArrowLeftIcon size={18} aria-hidden="true" />
                <span>
                  <span className="nav-btn-title">{previous.label}</span>
                  <span className="nav-btn-sub">{previous.subtitle}</span>
                </span>
              </button> :

            <span />
            }

            <div className="inline-actions" style={{ alignItems: 'center' }}>
              <span className="save-state" aria-live="polite">
                {lastSaved ?
                <>
                    <CheckCircle2Icon size={14} aria-hidden="true" />
                    Saved at {lastSaved}
                  </> :

                'Not saved yet'
                }
              </span>
              <button type="button" className="btn btn--secondary" onClick={save}>
                <SaveIcon size={16} aria-hidden="true" />
                Save Data
              </button>
              {next ?
              <button
                type="button"
                className="nav-btn nav-btn--primary"
                onClick={next.onClick}>

                  <span>
                    <span className="nav-btn-title">{next.label}</span>
                    <span className="nav-btn-sub">{next.subtitle}</span>
                  </span>
                  <ArrowRightIcon size={18} aria-hidden="true" />
                </button> :
              null}
            </div>
          </div>
        </main>
      </div>
    </div>);

}
